import { cx } from "@suse-o11y/class-variance";
import { DialogBoundary, DialogClose, DialogTrigger, useDialogClose } from "@suse-o11y/dialog";
import { filter } from "@suse-o11y/filter";
import { IconDashboard } from "@suse-o11y/icons";
import { matchIdentifiable } from "@suse-o11y/identifiable";
import { piped } from "@suse-o11y/piped";
import { prop } from "@suse-o11y/prop";
import { sortBy } from "@suse-o11y/sort-by";
import React from "react";

import { Alert } from "~/components/alert";
import { SkeletonList } from "~/components/skeleton/list";

import type { DashboardWidgetSpecInitConfig } from "../../common/dashboard-spec";
import { useParams } from "../../generic/routing";
import { queryAllFullDashboards, useOptimisticUpdateDashboard } from "../../queries/dashboards";
import { useQuery } from "../../queries/react-query";
import { useDashboardsPermissions } from "../../routes/dashboards/permissions";
import type { DASHBOARD_PATH } from "../../routes/Routes";
import { ErrorMessage } from "../_notification-error";
import { SetupDashboardDrawer } from "../_setup-dashboard-drawer";
import { DashboardStar } from "../dashboard-star";
import { GenericErrorMessage } from "../react/error/GenericErrorMessage";
import { DONT_AUTO_DISMISS, notification } from "../react/notification/Notification";

import { SuccessMessage } from "./_success-message";

const HEADING_CLASS = cx("font-medium text-15/5.5 text-grey-900");

const DASHBOARD_ENTRY_CLASS = cx(
	"group pane not-disabled:pane-interactive flex flex-row items-center gap-3 p-4 text-13/4 text-grey-800",
);

export interface AddToDashboardContentProps {
	widgets: ReadonlyArray<DashboardWidgetSpecInitConfig>;
}

export const AddToDashboardContent: React.FC<AddToDashboardContentProps> = ({ widgets }) => {
	const { idOrIdentifier: currentDashboardIdentifier } = useParams<typeof DASHBOARD_PATH>();
	const close = useDialogClose();

	const { hasCreateDashboardsPermission, hasUpdateDashboardsPermission } = useDashboardsPermissions().getOrElse({
		hasCreateDashboardsPermission: false,
		hasUpdateDashboardsPermission: () => false,
	});

	const {
		data: dashboards,
		error: dashboardsError,
		refetch: refetchDashboards,
	} = useQuery({
		...queryAllFullDashboards,
		select: piped(filter(hasUpdateDashboardsPermission), sortBy([prop("lastUpdateTimestamp"), "desc"])),
	});

	const { mutate: updateDashboard } = useOptimisticUpdateDashboard({
		onSuccess: ({ id, name }) => {
			notification.success({
				duration: 5,
				message: (
					<SuccessMessage addedWidgetsCount={widgets.length} dashboardId={id} dashboardName={name} type="existing" />
				),
			});
		},

		onError: (error) => {
			notification.error({
				duration: DONT_AUTO_DISMISS,
				message: (
					<ErrorMessage error={error}>Something went wrong while adding new widgets to the dashboard.</ErrorMessage>
				),
			});
		},
	});

	if (dashboardsError != null) {
		return <GenericErrorMessage error={dashboardsError} onRetry={() => refetchDashboards()} />;
	}

	return (
		<div className="flex flex-col gap-6">
			{hasCreateDashboardsPermission && (
				<section className="flex flex-col gap-3">
					<h3 className={HEADING_CLASS}>New dashboard</h3>

					<DialogBoundary>
						<DialogTrigger className={DASHBOARD_ENTRY_CLASS} disabled={dashboards == null}>
							<IconDashboard className="group-disabled:skeleton shrink-0" />
							<span className="group-disabled:skeleton">Add new dashboard</span>
						</DialogTrigger>

						<SetupDashboardDrawer
							initial={{ name: "", description: "", starred: false, publiclyAccessible: false }}
							onSuccess={({ id, name }) => {
								notification.success({
									duration: 5,
									message: (
										<SuccessMessage
											addedWidgetsCount={widgets.length}
											dashboardId={id}
											dashboardName={name}
											type="new"
										/>
									),
								});
								close();
							}}
							type="create"
							widgets={widgets}
						/>
					</DialogBoundary>
				</section>
			)}

			{dashboards?.length !== 0 && (
				<section className="flex flex-col gap-3">
					<h3 className={HEADING_CLASS}>Last used dashboards</h3>

					<SkeletonList className="flex flex-col gap-[3px]" count={5} each={dashboards} el="div">
						{(dashboard) => (
							<DialogClose
								className={DASHBOARD_ENTRY_CLASS}
								disabled={dashboard == null}
								key={dashboard?.id}
								onClick={() => {
									if (dashboard != null) {
										updateDashboard({
											idOrIdentifier: dashboard.id,
											diff: {
												spec: dashboard.spec.addWidgets(widgets),
											},
										});
									}
								}}
							>
								<IconDashboard className="group-disabled:skeleton shrink-0" />

								<span className="flex flex-1 flex-row">
									<span className="group-disabled:skeleton group-disabled:flex-1">{dashboard?.name}</span>

									<DashboardStar className="ml-2 size-4" id={dashboard?.id} />

									{dashboard != null &&
										currentDashboardIdentifier != null &&
										matchIdentifiable(dashboard, currentDashboardIdentifier) && (
											<span className="ml-4 flex-1 text-right font-normal text-11 text-grey-500 uppercase">
												Current
											</span>
										)}
								</span>
							</DialogClose>
						)}
					</SkeletonList>
				</section>
			)}

			{dashboards?.length === 0 && !hasCreateDashboardsPermission && (
				<Alert>Unfortunately, there are no available dashboards you can add this widget to.</Alert>
			)}
		</div>
	);
};
